import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Smartphone,
  Shield,
  Zap,
  ArrowRight,
  Cloud,
  RefreshCw,
  Wifi,
  Database,
  Settings,
  Users,
  Bell,
  Map,
  TrendingUp,
  Lock,
  CheckCircle,
  Camera,
  Clock,
  MessageSquare,
  Truck
} from 'lucide-react';

const MetricCard = ({ icon: Icon, value, label, trend }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="flex flex-col items-center gap-4 mb-4">
      <div className="p-3 rounded-lg bg-indigo-500/10">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      {trend && (
        <div className="bg-green-500/10 px-2 py-1 rounded-full flex items-center gap-1">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm">{trend}</span>
        </div>
      )}
    </div>
    <div className="text-3xl font-bold text-white mb-2">{value}</div>
    <p className="text-gray-400">{label}</p>
  </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-indigo-400" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-300">{description}</p>
  </motion.div>
);

const MobileApplications = () => {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
          </div>
  
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-block mb-6">
                <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1.5 border border-indigo-500/20">
                  <Smartphone className="w-4 h-4" />
                  Driver Mobile App
                </div>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Empower Your Drivers on the Road
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Real-time load tracking, route optimization, and seamless communication 
                all in one powerful driver-focused mobile application.
              </p>
  
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Request Demo
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
  
        {/* Metrics Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <MetricCard
                icon={Truck}
                value="50K+"
                label="Active Drivers"
                trend="+28%"
              />
              <MetricCard
                icon={Map}
                value="99.9%"
                label="Location Accuracy"
              />
              <MetricCard
                icon={Clock}
                value="<30sec"
                label="Update Frequency"
              />
            </div>
          </div>
        </section>
  
        {/* Features Grid */}
        <section className="py-20 bg-gray-900/50">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center max-w-3xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Driver-Focused Features
              </h2>
              <p className="text-gray-300">
                Everything your drivers need for efficient load management
              </p>
            </motion.div>
  
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard
                icon={Map}
                title="Real-Time Navigation"
                description="Turn-by-turn directions with traffic updates and route optimization for maximum efficiency."
              />
              <FeatureCard
                icon={Bell}
                title="Load Updates"
                description="Instant notifications for load assignments, route changes, and delivery updates."
              />
              <FeatureCard
                icon={MessageSquare}
                title="In-App Communication"
                description="Direct messaging with dispatch and automated status updates to all stakeholders."
              />
              <FeatureCard
                icon={Wifi}
                title="Offline Mode"
                description="Continuous operation in areas with limited connectivity, with automatic sync when back online."
              />
              <FeatureCard
                icon={Camera}
                title="Document Capture"
                description="Easy upload of BOLs, delivery receipts, and other load documents with instant sync."
              />
              <FeatureCard
                icon={Clock}
                title="Hours of Service"
                description="Built-in HOS tracking and compliance monitoring to keep drivers within regulations."
              />
            </div>
          </div>
        </section>
  
        {/* Driver Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">Driver Experience</h3>
                <p className="text-gray-400 mt-1">Why drivers love our mobile app</p>
              </div>
              <div className="p-6 grid gap-6">
                {[
                  {
                    title: "User-Friendly Interface",
                    description: "Simple, intuitive design that's easy to use while on the road."
                  },
                  {
                    title: "Streamlined Workflows",
                    description: "Reduce paperwork and administrative tasks with digital documentation."
                  },
                  {
                    title: "Real-Time Support",
                    description: "Direct connection to dispatch and support teams when needed."
                  },
                  {
                    title: "Smart Load Planning",
                    description: "Optimized routes and schedules to maximize driving time and earnings."
                  }
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    className="flex gap-4 items-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{benefit.title}</h4>
                      <p className="text-gray-300 mt-1">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
  
        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Optimize Your Fleet?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Join thousands of drivers already using our mobile app for seamless load management
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Schedule Demo
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
};

export default MobileApplications;